<template>
	<button
		@click="togglePlayback"
	>
		<EwIcon
			v-if="!isLoading"
			:icon="isPlaying ? 'tabler:player-pause' : 'tabler:player-play'"
			class="cursor-pointer w-5 h-5 md:text-base md:mr-2"
		/>
		<div v-else class="spinner-player-btn w-5 h-5 md:mr-2"></div>
		<div v-if="displayType === 'list'">
			<span class="hidden md:inline">{{ labels.playlabel }}</span>
		</div>
		<div v-if="displayType === 'grid'">
			{{displayType}}
		</div>
	</button>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, onMounted, defineProps, defineEmits } from 'vue';
import { messages } from '@/assets/language';
import { useAppStore } from '@/stores/app-store'


const { currentLng } = useAppStore()
const labels = computed( () => {
	const key = currentLng.value as keyof typeof messages
	return messages[key] ?? {}
});

const displayType = ref<"list" | "grid">( "list" );

const props = defineProps<{
	file_url?: string;
	isPlaying?: boolean;
	language?: string;
}>();

const emit = defineEmits(['togglePlayback']);

//@ts-ignore

const sound = ref<Howl | null>(null);
const isLoading = ref(false);

const teardownAudio = () => {
	if (!sound.value) {
		return;
	}
	try {
		sound.value.stop();
		(sound.value as any).unload?.();
	} catch {
		// noop
	}
	sound.value = null;
	isLoading.value = false;
};

const playWhenReady = () => {
	if (!sound.value) {
		return;
	}

	try {
		const state = (sound.value as any).state?.();
		if (state && state !== 'loaded') {
			isLoading.value = true;
			if (typeof (sound.value as any).once === 'function') {
				(sound.value as any).once('load', () => {
					if (props.isPlaying) {
						sound.value?.play();
					}
				});
			}
			return;
		}
	} catch {
		// fallback below
	}

	sound.value.play();
};

const initAudio = () => {
	if (!props.file_url) {
		isLoading.value = false;
		return;
	}
	isLoading.value = true;
	//@ts-ignore
	sound.value = new Howl({
		src: [props.file_url],
		preload: true,
		volume: 0.5,
		onplay: () => {},
		onpause: () => {},
		onend: () => {
			emit('togglePlayback');
		},
		onload: () => {
			isLoading.value = false; // Ndalon ngarkimi kur është i ngarkuar
			if (props.isPlaying) {
				sound.value?.play();
			}
		},
		onloaderror: () => {
			isLoading.value = false; // Ndalon ngarkimi në rast të gabimit
		},
		onplayerror: () => {
			isLoading.value = false; // Ndalon ngarkimi në rast të gabimit
		},
	});
};

const stopFromGlobalEvent = () => {
	teardownAudio();
	if (props.isPlaying) {
		emit('togglePlayback');
	}
};

watch(
	() => props.isPlaying,
	(newValue) => {
		if (newValue) {
			if (!sound.value) {
				initAudio();
				return;
			}
			playWhenReady();
		} else if (sound.value) {
			// Always reset to the beginning when playback is stopped.
			sound.value.stop();
		}
	}
);

watch(
	() => props.language,
	() => {
		teardownAudio();
		if (props.isPlaying) {
			emit('togglePlayback');
		}
	}
);

watch(
	() => props.file_url,
	(newUrl, oldUrl) => {
		if (newUrl === oldUrl) {
			return;
		}
		const shouldResume = Boolean(props.isPlaying);
		teardownAudio();
		if (shouldResume) {
			initAudio();
		}
	}
);

onBeforeUnmount(() => {
	teardownAudio();
	window.removeEventListener('stop-inline-audio', stopFromGlobalEvent);
});

onMounted(() => {
	window.addEventListener('stop-inline-audio', stopFromGlobalEvent);
});

const togglePlayback = () => {
	emit('togglePlayback');
};
</script>
<style scoped>
.spinner-player-btn {
	border: 4px solid rgba(255, 255, 255, 0.1);
	border-left-color: #fff;
	border-radius: 50%;
	width: 1.25rem;
	height: 1.25rem;
	animation: spin 1s infinite linear;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
